/**
 * RELÓGIO DO SERVIDOR — offset entre o relógio local do device e o do servidor.
 *
 * <p>O sync canônico de videowall resolve a posição no loop como
 * `(agora − época) % duraçãoDoLoop`. Enquanto "agora" for o `Date.now()` do device,
 * dois players com relógios diferentes ficam defasados **exatamente** por essa
 * diferença, para sempre — nenhuma outra parte do sistema corrige isso. Medido em
 * campo: devices da mesma frota chegam a divergir alguns segundos, e isso aparece na
 * tela como conteúdos trocando fora de compasso.</p>
 *
 * <p>Duas fontes alimentam o offset, com confianças diferentes:</p>
 * <ul>
 *   <li><b>Amostra por round-trip</b> ({@link recordSyncSample}): toda resposta de sync traz
 *   `sync_timestamp`; cronometrando a requisição dá pra estimar o instante do servidor no
 *   MEIO da chamada (NTP simplificado). Erro residual de dezenas de ms. É a fonte de
 *   <b>alta</b> confiança.</li>
 *   <li><b>Batida anunciada</b> ({@link applyServerTime}): o servidor diz "são X horas" pelo
 *   SSE a cada 15s. Não há como medir o atraso de entrega — e ele só atrasa, nunca adianta.
 *   Medido no próprio repo: p95 de 12s a 10k telas. É a fonte de <b>baixa</b> confiança:
 *   entra por uma janela, usa a batida MENOS atrasada e só provoca salto quando várias
 *   consecutivas concordam.</li>
 * </ul>
 *
 * <p>Por que isso importa (medido no grupo 15, out/2026): com a batida tratada como amostra
 * precisa, UMA batida atrasada > 2s substituía o relógio inteiro na hora, e o player
 * saltava de slot sem nada ter mudado. E o offset restaurado do disco valia como amostra
 * fresca mesmo depois de um dia desligado — a primeira amostra boa pesava só 30%.</p>
 *
 * <p>O offset é persistido: no boot seguinte o player já parte corrigido, antes mesmo
 * do primeiro sync responder. Mas um offset velho é só um <i>palpite</i>: a primeira
 * amostra real o substitui inteira.</p>
 */

const STORAGE_KEY = 'efflux_server_clock_offset_v1';

/** Amostra com RTT acima disto é ruído de rede — não serve pra estimar relógio. */
const MAX_RTT_MS = 5_000;

/** Diferença que caracteriza salto de relógio (ex.: NTP do device acertou) → adota na hora. */
const JUMP_THRESHOLD_MS = 2_000;

/** Peso da amostra nova no alisamento. Baixo = estável, alto = responsivo. */
const SMOOTHING = 0.3;

/**
 * Offset restaurado do disco com mais idade que isto é palpite, não amostra. O RTC de um
 * tablet deriva alguns segundos por dia; depois de uma noite desligado o valor gravado já
 * não representa o relógio atual.
 */
const PERSISTED_MAX_AGE_MS = 6 * 60 * 60_000;

/** Quantas batidas recentes entram na escolha da "menos atrasada". */
const BEAT_WINDOW = 5;

/** Batidas consecutivas que precisam concordar pra uma batida provocar salto. */
const BEAT_STEP_QUORUM = 3;

/** Duas batidas "concordam" quando diferem menos que isto. */
const BEAT_AGREE_MS = 1_000;

/** Período do vigia do relógio do device. */
const DEVICE_CLOCK_WATCH_MS = 5_000;

/** Variação do relógio de parede frente ao monotônico que caracteriza salto/sono. */
const DEVICE_CLOCK_JUMP_MS = 1_000;

export type ClockSampleSource = 'rtt' | 'beat' | 'persisted';

interface PersistedOffset {
  offsetMs: number;
  savedAt: number;
}

export interface ClockDiagnostics {
  /** Servidor − device, em ms. Positivo = device atrasado. */
  offsetMs: number;
  /** De onde veio o valor atual. `null` = nunca houve amostra (offset 0). */
  source: ClockSampleSource | null;
  /** Idade da última amostra aceita, em ms. `null` sem amostra. */
  sampleAgeMs: number | null;
  /** RTT da última amostra por round-trip. `null` se a última foi batida/disco. */
  lastRttMs: number | null;
  /** `true` enquanto o offset for só o palpite do disco ou tiver sido invalidado. */
  tentative: boolean;
}

let offsetMs = 0;
let hasSample = false;
let source: ClockSampleSource | null = null;
let sampleAt: number | null = null;
let lastRttMs: number | null = null;

/** Últimas batidas, em ordem de chegada (amostra bruta = servidor − device). */
let beatWindow: number[] = [];

type InvalidationListener = () => void;
const invalidationListeners = new Set<InvalidationListener>();

/** Carrega o último offset conhecido. Best-effort — nunca pode quebrar o boot. */
function restore(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as PersistedOffset;
    if (
      typeof parsed?.offsetMs !== 'number' ||
      !Number.isFinite(parsed.offsetMs)
    ) {
      return;
    }
    offsetMs = parsed.offsetMs;
    source = 'persisted';
    // Sem `savedAt` (formato antigo) a idade é desconhecida → trata como velho.
    const age =
      typeof parsed.savedAt === 'number' && Number.isFinite(parsed.savedAt)
        ? Date.now() - parsed.savedAt
        : Infinity;
    // Fresco: vale como amostra (a próxima só alisa). Velho: é palpite — a próxima
    // amostra real substitui inteira, em vez de pesar 30% contra um valor de ontem.
    hasSample = age >= 0 && age <= PERSISTED_MAX_AGE_MS;
    sampleAt = hasSample ? parsed.savedAt : null;
  } catch {
    // localStorage indisponível (modo privado, storage cheio) — segue com offset 0.
  }
}

function persist(): void {
  try {
    const payload: PersistedOffset = { offsetMs, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // best-effort
  }
}

function accept(
  value: number,
  from: ClockSampleSource,
  rtt: number | null,
): void {
  offsetMs = value;
  hasSample = true;
  source = from;
  sampleAt = Date.now();
  lastRttMs = rtt;
  persist();
}

function parseServerTime(
  value: string | number | undefined | null,
): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Aceita ISO ("2026-10-08T12:00:00Z") e epoch em ms como string ("1791000000000").
  const asNumber = /^\d{12,}$/.test(value) ? Number(value) : Date.parse(value);
  return Number.isFinite(asNumber) ? asNumber : null;
}

/**
 * Registra uma amostra a partir de uma resposta cronometrada (alta confiança).
 *
 * @param serverTimestamp hora do servidor carimbada na resposta (ISO ou epoch ms)
 * @param requestSentAt   `Date.now()` imediatamente ANTES de disparar a requisição
 * @param responseAt      `Date.now()` imediatamente DEPOIS de receber a resposta
 * @returns true se a amostra foi aceita
 */
export function recordSyncSample(
  serverTimestamp: string | number | undefined | null,
  requestSentAt: number,
  responseAt: number,
): boolean {
  const serverMs = parseServerTime(serverTimestamp);
  if (serverMs === null) return false;

  const rtt = responseAt - requestSentAt;
  // RTT negativo = relógio local mexeu no meio da chamada; alto demais = rede ruim.
  if (rtt < 0 || rtt > MAX_RTT_MS) return false;

  // Instante local equivalente ao momento em que o servidor carimbou a resposta.
  const localMidpoint = requestSentAt + rtt / 2;
  const sample = serverMs - localMidpoint;

  if (!hasSample || Math.abs(sample - offsetMs) > JUMP_THRESHOLD_MS) {
    // Primeira amostra, palpite do disco, ou o relógio saltou de verdade — adotar
    // direto. Alisar aqui faria o player levar vários syncs pra reconhecer um acerto.
    accept(sample, 'rtt', rtt);
  } else {
    accept(offsetMs * (1 - SMOOTHING) + sample * SMOOTHING, 'rtt', rtt);
  }
  // Uma amostra precisa supera qualquer histórico de batidas.
  beatWindow = [];
  return true;
}

/**
 * Hora do servidor ANUNCIADA (batida de sincronismo) — baixa confiança.
 *
 * <p>O atraso de entrega do SSE só faz a amostra parecer <i>menor</i> (o servidor carimbou
 * antes, o device leu depois). Logo, entre as últimas batidas, a MAIOR é a menos atrasada
 * — é ela que vale, não a média (mesma ideia do filtro de menor atraso do NTP).</p>
 *
 * <p>Salto só quando {@link BEAT_STEP_QUORUM} batidas consecutivas discordam do offset
 * atual na mesma direção e concordam entre si. Uma batida atrasada sozinha, por maior que
 * seja o atraso, não mexe no relógio além do alisamento.</p>
 */
export function applyServerTime(serverTimestamp: string | number): boolean {
  const serverMs = parseServerTime(serverTimestamp);
  if (serverMs === null) return false;

  const sample = serverMs - Date.now();
  beatWindow.push(sample);
  if (beatWindow.length > BEAT_WINDOW) beatWindow.shift();

  if (!hasSample) {
    // Sem referência nenhuma (boot sem disco, ou invalidado): melhor a batida que nada.
    accept(sample, 'beat', null);
    return true;
  }

  const leastDelayed = Math.max(...beatWindow);
  if (Math.abs(leastDelayed - offsetMs) <= JUMP_THRESHOLD_MS) {
    accept(offsetMs * (1 - SMOOTHING) + leastDelayed * SMOOTHING, 'beat', null);
    return true;
  }

  const recent = beatWindow.slice(-BEAT_STEP_QUORUM);
  const quorum =
    recent.length === BEAT_STEP_QUORUM &&
    recent.every((b) => Math.abs(b - offsetMs) > JUMP_THRESHOLD_MS) &&
    recent.every(
      (b) => Math.sign(b - offsetMs) === Math.sign(recent[0] - offsetMs),
    ) &&
    Math.max(...recent) - Math.min(...recent) <= BEAT_AGREE_MS;
  if (quorum) {
    accept(Math.max(...recent), 'beat', null);
    return true;
  }
  // Discordante e sem quórum: provavelmente atraso de entrega. Guardada na janela, ignorada.
  return false;
}

/**
 * O relógio de parede do device saltou (NTP acertou, usuário mexeu) ou o aparelho dormiu.
 * O offset guardado deixa de ser confiável: vira palpite e quem assinou
 * {@link onClockInvalidated} deve providenciar uma amostra nova na hora.
 */
function invalidate(): void {
  hasSample = false;
  beatWindow = [];
  invalidationListeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // um assinante quebrado não pode derrubar o vigia
    }
  });
}

/**
 * Vigia o relógio de parede contra o monotônico. `Date.now() − performance.now()` é
 * constante enquanto nada acontece; muda quando o device acerta a hora ou dorme (o
 * monotônico para em alguns aparelhos). Nos dois casos o offset virou palpite.
 */
let watchSkew: number | null = null;
function watchDeviceClock(): void {
  if (
    typeof performance?.now !== 'function' ||
    typeof setInterval !== 'function'
  )
    return;
  watchSkew = Date.now() - performance.now();
  setInterval(() => {
    const skew = Date.now() - performance.now();
    if (
      watchSkew !== null &&
      Math.abs(skew - watchSkew) > DEVICE_CLOCK_JUMP_MS
    ) {
      invalidate();
    }
    watchSkew = skew;
  }, DEVICE_CLOCK_WATCH_MS);
}

/** Assina a invalidação do relógio. Retorna a função de cancelamento. */
export function onClockInvalidated(listener: InvalidationListener): () => void {
  invalidationListeners.add(listener);
  return () => {
    invalidationListeners.delete(listener);
  };
}

/** "Agora" corrigido pelo relógio do servidor. Use no lugar de `Date.now()` no sync de loop. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

/** Offset atual em ms (servidor − device). Positivo = device atrasado. Para diagnóstico. */
export function getClockOffsetMs(): number {
  return offsetMs;
}

/** Se há uma amostra válida (própria, ou restaurada do disco e ainda fresca). */
export function hasClockSample(): boolean {
  return hasSample;
}

/** Estado do relógio pro relatório de playback — o painel distingue desvio de relógio do de sequência. */
export function getClockDiagnostics(): ClockDiagnostics {
  return {
    offsetMs,
    source,
    sampleAgeMs: sampleAt === null ? null : Math.max(0, Date.now() - sampleAt),
    lastRttMs,
    tentative: !hasSample,
  };
}

/** Somente para testes. */
export function __resetServerClock(): void {
  offsetMs = 0;
  hasSample = false;
  source = null;
  sampleAt = null;
  lastRttMs = null;
  beatWindow = [];
  invalidationListeners.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/** Somente para testes: reexecuta a restauração do disco. */
export function __restoreServerClockForTest(): void {
  restore();
}

/** Somente para testes: simula o vigia detectando salto do relógio do device. */
export function __invalidateServerClockForTest(): void {
  invalidate();
}

restore();
watchDeviceClock();
